import {field} from "../../stem-core/state/StoreField";
import {BaseStore, globalStore} from "../../stem-core/state/Store";
import {apiClient, type MerchantScopedRequest} from "../connection/BlinkApiClient";
import type {StoreId} from "../../stem-core/state/State";
import {BaseEnum, makeEnum} from "../../stem-core/state/BaseEnum";
import {Merchant} from "./MerchantStore";
import {MerchantEmailSender} from "./merchant/MerchantEmailSender";
import {MerchantEmailCampaign} from "./merchant/MerchantEmailCampaignStore";


@makeEnum
export class EmailTemplateType extends BaseEnum {
    static CUSTOM; // A custom merchant defined template
    static CAMPAIGN;
    static AUTH_LOGIN_CODE;
    static CHANGE_EMAIL_CONFIRM;
    static CHANGE_EMAIL_UNDO;
    static DONATION_SUCCESS;
    static DONATION_CANCEL;
    static SUBSCRIPTION_SUCCESS;
    static SUBSCRIPTION_GIFT_SUCCESS_BILLED_USER;
    static SUBSCRIPTION_GIFT_SUCCESS_BENEFICIARY;
}


@globalStore
export class EmailTemplate extends BaseStore("EmailTemplate") {
    @field(String) name: string;
    @field(EmailTemplateType) type: EmailTemplateType;
    @field("Merchant") merchant: Merchant;
    @field("MerchantEmailSender") emailSender: MerchantEmailSender;
    @field("MerchantEmailCampaign") campaign: MerchantEmailCampaign;
    @field(String) replyTo: string;
    @field(Array) bccEmails: string[];
    @field(String) subject: string;
    @field(String) html: string;
    @field(String) plainText: string;
    @field(Boolean) isTypeDefault: boolean;

    toString(): string {
        return this.name;
    }
}


// CreateEmailTemplateRequest (api/mailing/views.py)
export interface CreateEmailTemplateRequest extends MerchantScopedRequest {
    type: EmailTemplateType;
    name?: string;
    campaignId?: StoreId;
    fromTemplateId?: StoreId;
}

export async function apiCreateEmailTemplate(request: CreateEmailTemplateRequest): Promise<EmailTemplate> {
    const response = await apiClient.post("/mailing/create_template", request);
    return EmailTemplate.loadObject(response);
}

// UpdateEmailTemplateRequest, whose templateId comes from the EmailTemplateRequest base
export interface UpdateEmailTemplateRequest extends MerchantScopedRequest {
    templateId: StoreId;
    name?: string;
    emailSenderId?: StoreId;
    replyTo?: string;
    bccEmails?: string[];
    subject?: string;
    html?: string;
    plainText?: string;
}

export async function apiUpdateEmailTemplate(request: UpdateEmailTemplateRequest): Promise<any> {
    return apiClient.post("/mailing/update_template", request);
}

// PreviewEmailTemplateRequest (api/mailing/views.py)
export interface PreviewEmailTemplateRequest extends MerchantScopedRequest {
    subject: string;
    html: string;
    emailType: EmailTemplateType;
    context: Record<string, any>;
    plainText?: string;
}

export async function apiRenderEmailTemplate(data: PreviewEmailTemplateRequest): Promise<any> {
    return apiClient.post("/mailing/render_template", data);
}

// SendTestEmailRequest extends the preview request with who it goes out as and to
export interface SendTestEmailRequest extends PreviewEmailTemplateRequest {
    emailSenderId?: StoreId;
    replyTo?: string;
    bccEmails?: string[];
}

export async function apiSendTestEmailTemplate(data: SendTestEmailRequest): Promise<any> {
    return apiClient.post("/mailing/send_test_email", data);
}
